/**
 * ORYXOS Client-Side Security Utilities
 *
 * Provides browser-safe security functions for:
 *  - HTML sanitization (DOMPurify-style)
 *  - Safe JSON parsing with validation
 *  - Secure storage with TTL
 *  - URL validation
 *  - XSS detection
 *  - CSP nonce generation
 *  - Clickjacking prevention
 *
 * IMPORTANT: This file must only use browser APIs (no Node.js APIs).
 */

// ── HTML Sanitization ─────────────────────────────────────────────────

/** Tags that are completely stripped along with their content */
const DANGEROUS_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'applet',
  'form', 'meta', 'link', 'base', 'noscript',
])

/** Attributes that are always removed (event handlers + dangerous attrs) */
const DANGEROUS_ATTRS = /^on/i
const DANGEROUS_ATTR_VALUES = /^(javascript|data|vbscript):/i

/** Allowed tags for safe HTML */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'hr', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'a', 'strong', 'em', 'b', 'i', 'u', 's', 'code',
  'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'figure', 'figcaption', 'details', 'summary',
])

/** Allowed attributes per tag */
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title', 'target', 'rel']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan', 'scope']),
}

/**
 * Sanitize HTML string for safe rendering in React.
 * Strips script tags, event handlers, and javascript: URLs.
 * Only allows a safe subset of HTML tags and attributes.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(html: string): string {
  if (typeof html !== 'string' || !html) return ''

  // Create a detached document for parsing
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  function cleanNode(node: Node): void {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      const tagName = el.tagName.toLowerCase()

      // Remove dangerous tags entirely (including their content)
      if (DANGEROUS_TAGS.has(tagName)) {
        el.parentNode?.removeChild(el)
        return
      }

      // Remove tags not in the allowlist (but keep their children)
      if (!ALLOWED_TAGS.has(tagName)) {
        while (el.firstChild) {
          el.parentNode?.insertBefore(el.firstChild, el)
        }
        el.parentNode?.removeChild(el)
        return
      }

      // Remove dangerous attributes
      const attrs = Array.from(el.attributes)
      for (const attr of attrs) {
        const attrName = attr.name.toLowerCase()
        const attrValue = attr.value.trim()

        // Remove event handlers (on*)
        if (DANGEROUS_ATTRS.test(attrName)) {
          el.removeAttribute(attr.name)
          continue
        }

        // Remove javascript: and data: URLs in attribute values
        if (DANGEROUS_ATTR_VALUES.test(attrValue)) {
          el.removeAttribute(attr.name)
          continue
        }

        // Check if attribute is allowed for this tag
        const tagAllowedAttrs = ALLOWED_ATTRS[tagName]
        if (tagAllowedAttrs && !tagAllowedAttrs.has(attrName)) {
          // Allow data-* and aria-* attributes
          if (!attrName.startsWith('data-') && !attrName.startsWith('aria-')) {
            el.removeAttribute(attr.name)
          }
        } else if (!tagAllowedAttrs) {
          // For tags without specific attr rules, only keep data-* and aria-*
          if (!attrName.startsWith('data-') && !attrName.startsWith('aria-')) {
            el.removeAttribute(attr.name)
          }
        }
      }

      // Add rel="noopener noreferrer" to all links with target="_blank"
      if (tagName === 'a' && el.getAttribute('target') === '_blank') {
        el.setAttribute('rel', 'noopener noreferrer')
      }
    }

    // Recursively clean child nodes (iterate copy to handle removal)
    if (node.childNodes) {
      const children = Array.from(node.childNodes)
      for (const child of children) {
        cleanNode(child)
      }
    }
  }

  cleanNode(doc.body)

  return doc.body.innerHTML
}

// ── Safe JSON Parsing ─────────────────────────────────────────────────

/**
 * Safely parse a JSON string with validation.
 *
 * @param str - The JSON string to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed value or defaultValue on failure
 */
export function safeJsonParse<T = unknown>(str: string, defaultValue: T): T {
  if (typeof str !== 'string' || !str.trim()) return defaultValue

  try {
    const parsed = JSON.parse(str)

    // Basic validation: reject if parsed result is undefined
    if (parsed === undefined) return defaultValue

    return parsed as T
  } catch {
    return defaultValue
  }
}

// ── Secure Storage ────────────────────────────────────────────────────

const SECURE_STORAGE_PREFIX = 'oryxos_sec_'
const SENSITIVE_KEYS = [
  'token', 'session', 'password', 'secret', 'credential',
  'auth', 'key', 'private', 'access',
]

/**
 * Clear localStorage and sessionStorage of sensitive data on logout.
 * Only removes entries whose keys contain sensitive keywords or our prefix.
 */
export function clearSensitiveStorage(): void {
  try {
    // Clear our secure storage items from sessionStorage
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i)
      if (key && (key.startsWith(SECURE_STORAGE_PREFIX) || SENSITIVE_KEYS.some(k => key.toLowerCase().includes(k)))) {
        sessionStorage.removeItem(key)
      }
    }

    // Clear sensitive items from localStorage
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i)
      if (key && SENSITIVE_KEYS.some(k => key.toLowerCase().includes(k))) {
        localStorage.removeItem(key)
      }
    }
  } catch {
    // Storage access may be blocked in some contexts
  }
}

interface SecureStorageItem {
  value: string
  expiresAt: number // timestamp in ms, 0 = never expires
  createdAt: number
}

/**
 * Store a value in sessionStorage with TTL.
 * Uses sessionStorage (cleared on tab close) for security.
 *
 * @param key   - Storage key
 * @param value - Value to store
 * @param ttlMs - Time-to-live in milliseconds (default: 30 minutes)
 */
export function setSecureItem(key: string, value: string, ttlMs: number = 30 * 60 * 1000): void {
  try {
    const item: SecureStorageItem = {
      value: btoa(encodeURIComponent(value)), // Basic obfuscation
      expiresAt: ttlMs > 0 ? Date.now() + ttlMs : 0,
      createdAt: Date.now(),
    }
    sessionStorage.setItem(SECURE_STORAGE_PREFIX + key, JSON.stringify(item))
  } catch {
    // Storage may be full or blocked
  }
}

/**
 * Retrieve a value from sessionStorage with TTL check.
 * Returns null if expired or not found.
 *
 * @param key - Storage key
 * @returns The stored value, or null if expired/not found
 */
export function getSecureItem(key: string): string | null {
  try {
    const raw = sessionStorage.getItem(SECURE_STORAGE_PREFIX + key)
    if (!raw) return null

    const item: SecureStorageItem = JSON.parse(raw)

    // Check TTL
    if (item.expiresAt > 0 && Date.now() > item.expiresAt) {
      sessionStorage.removeItem(SECURE_STORAGE_PREFIX + key)
      return null
    }

    return decodeURIComponent(atob(item.value))
  } catch {
    return null
  }
}

/**
 * Remove a specific item from secure storage.
 *
 * @param key - Storage key to remove
 */
export function removeSecureItem(key: string): void {
  try {
    sessionStorage.removeItem(SECURE_STORAGE_PREFIX + key)
  } catch {
    // Ignore
  }
}

// ── URL Validation ────────────────────────────────────────────────────

/**
 * Validate that a URL doesn't use dangerous protocols.
 * Blocks javascript:, data:, vbscript:, and file: URLs.
 *
 * @param url - The URL to validate
 * @returns true if the URL is safe (http, https, mailto, tel, or relative)
 */
export function isSafeUrl(url: string): boolean {
  if (typeof url !== 'string' || !url.trim()) return false

  const trimmed = url.trim().toLowerCase()

  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:']
  for (const proto of dangerousProtocols) {
    if (trimmed.startsWith(proto)) return false
  }

  // Allow safe protocols
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('?') ||
    trimmed.startsWith('./') ||
    trimmed.startsWith('../')
  ) {
    return true
  }

  // For relative URLs without prefix, try parsing
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

// ── HTML Entity Escaping ──────────────────────────────────────────────

/**
 * Escape HTML entities for safe rendering.
 * Converts characters that could be interpreted as HTML markup.
 *
 * @param str - The string to escape
 * @returns Escaped string safe for HTML context
 */
export function escapeHtml(str: string): string {
  if (typeof str !== 'string') return ''

  const escapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
  }

  return str.replace(/[&<>"']/g, (char) => escapeMap[char] || char)
}

// ── CSP Nonce Generation ──────────────────────────────────────────────

/**
 * Generate a Content Security Policy nonce for inline scripts.
 * Uses Web Crypto API for cryptographically secure randomness.
 *
 * @returns A base64-encoded random nonce string
 */
export function generateNonce(): string {
  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
}

// ── XSS Detection ─────────────────────────────────────────────────────

/** Common XSS attack patterns */
const XSS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript\s*:/gi,
  /vbscript\s*:/gi,
  /on\w+\s*=/gi,            // event handlers: onclick=, onload=, etc.
  /expression\s*\(/gi,      // CSS expression()
  /url\s*\(\s*javascript:/gi,
  /data\s*:\s*text\/html/gi,
  /<\s*embed\b/gi,
  /<\s*object\b/gi,
  /<\s*iframe\b/gi,
  /<\s*applet\b/gi,
  /<\s*link\b[^>]*\bhref/gi,
  /<\s*meta\b/gi,
  /<\s*base\b/gi,
  /import\s*\(/gi,          // dynamic import injection
  /eval\s*\(/gi,
  /Function\s*\(/gi,
  /document\.(cookie|write|domain)/gi,
  /window\.(location|eval|setTimeout|setInterval)/gi,
]

/**
 * Detect common XSS patterns in user input.
 * Useful for flagging suspicious input before processing.
 *
 * @param input - The input string to check
 * @returns Object with detected flag and list of matched patterns
 */
export function detectXssAttempt(input: string): {
  detected: boolean
  patterns: string[]
} {
  if (typeof input !== 'string' || !input) {
    return { detected: false, patterns: [] }
  }

  const matchedPatterns: string[] = []

  for (const pattern of XSS_PATTERNS) {
    if (pattern.test(input)) {
      matchedPatterns.push(pattern.source)
    }
    // Reset regex lastIndex for global patterns
    pattern.lastIndex = 0
  }

  return {
    detected: matchedPatterns.length > 0,
    patterns: matchedPatterns,
  }
}

// ── Clickjacking Prevention ───────────────────────────────────────────

/**
 * Verify frame-ancestor compliance and detect clickjacking attempts.
 * Checks if the page is being framed and logs if so.
 *
 * @returns Object indicating if the page is framed (potential clickjacking)
 */
export function preventClickjacking(): {
  isFramed: boolean
  topWindowAccessible: boolean
} {
  let isFramed = false
  let topWindowAccessible = false

  try {
    isFramed = window.self !== window.top
  } catch {
    // If we can't access window.top, we're in a cross-origin iframe
    isFramed = true
  }

  try {
    if (window.top) {
      topWindowAccessible = true
    }
  } catch {
    topWindowAccessible = false
  }

  if (isFramed) {
    // Log potential clickjacking attempt
    console.warn(
      '[ORYXOS Security] Page is being rendered inside an iframe. ' +
      'This may be a clickjacking attempt. Ensure CSP frame-ancestors is configured.'
    )
  }

  return { isFramed, topWindowAccessible }
}
